import type { Stroke } from '../storage/schemas';

/** Deterministic PRNG so textured strokes look identical on every render and in exports. */
function seeded(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function path(ctx: CanvasRenderingContext2D, pts: number[], W: number, dx = 0, dy = 0): void {
  ctx.beginPath();
  if (pts.length < 3) return;
  ctx.moveTo(pts[0] * W + dx, pts[1] * W + dy);
  if (pts.length === 3) {
    ctx.lineTo(pts[0] * W + dx + 0.01, pts[1] * W + dy);
    return;
  }
  for (let i = 3; i < pts.length - 3; i += 3) {
    const mx = ((pts[i] + pts[i + 3]) / 2) * W + dx;
    const my = ((pts[i + 1] + pts[i + 4]) / 2) * W + dy;
    ctx.quadraticCurveTo(pts[i] * W + dx, pts[i + 1] * W + dy, mx, my);
  }
  const n = pts.length;
  ctx.lineTo(pts[n - 3] * W + dx, pts[n - 2] * W + dy);
}

function avgPressure(pts: number[]): number {
  let sum = 0;
  let n = 0;
  for (let i = 2; i < pts.length; i += 3) {
    sum += pts[i];
    n++;
  }
  return n ? sum / n : 0.5;
}

export function drawStroke(ctx: CanvasRenderingContext2D, s: Stroke, W: number): void {
  const pts = s.points;
  if (pts.length < 3) return;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = s.color;
  const p = avgPressure(pts);
  const width = Math.max(0.6, s.size * W * (0.65 + 0.7 * p));
  switch (s.tool) {
    case 'eraser':
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = s.size * W * 2.2;
      path(ctx, pts, W);
      ctx.stroke();
      break;
    case 'highlighter':
      ctx.globalAlpha = 0.38;
      ctx.lineCap = 'butt';
      ctx.lineWidth = s.size * W * 4;
      path(ctx, pts, W);
      ctx.stroke();
      break;
    case 'marker':
      ctx.globalAlpha = 0.9;
      ctx.lineWidth = width * 1.9;
      path(ctx, pts, W);
      ctx.stroke();
      break;
    case 'crayon': {
      // Several offset, semi-transparent passes give a waxy texture.
      const rnd = seeded(s.id);
      const passes = 5;
      for (let k = 0; k < passes; k++) {
        ctx.globalAlpha = 0.22 + rnd() * 0.18;
        ctx.lineWidth = width * (0.55 + rnd() * 0.6);
        const j = width * 0.35;
        path(ctx, pts, W, (rnd() - 0.5) * j, (rnd() - 0.5) * j);
        ctx.stroke();
      }
      break;
    }
    default:
      ctx.lineWidth = width;
      path(ctx, pts, W);
      ctx.stroke();
  }
  ctx.restore();
}

/** Render all strokes into a context whose drawing area is `W` px wide (height derives from the card). */
export function drawStrokes(ctx: CanvasRenderingContext2D, strokes: Stroke[], W: number): void {
  for (const s of strokes) drawStroke(ctx, s, W);
}

/** Reduce point count while keeping shape (radial distance filter, in card-width units). */
export function simplifyPoints(pts: number[], minDist = 0.0025): number[] {
  if (pts.length <= 6) return pts.map((v) => Math.round(v * 10000) / 10000);
  const out = [pts[0], pts[1], pts[2]];
  let lx = pts[0];
  let ly = pts[1];
  for (let i = 3; i < pts.length; i += 3) {
    const dx = pts[i] - lx;
    const dy = pts[i + 1] - ly;
    if (dx * dx + dy * dy >= minDist * minDist || i === pts.length - 3) {
      out.push(pts[i], pts[i + 1], pts[i + 2]);
      lx = pts[i];
      ly = pts[i + 1];
    }
  }
  return out.map((v) => Math.round(v * 10000) / 10000);
}
